import balloonsQrTemplateJson from "./templates/balloons.json";
import butterflyQrTemplateJson from "./templates/butterfly.json";
import cameraQrTemplateJson from "./templates/camera.json";
import defaultQrTemplateJson from "./templates/default.json";
import eucalyptusQrTemplateJson from "./templates/eucalyptus.json";
import flowerQrTemplateJson from "./templates/flower.json";
import gamerCardQrTemplateJson from "./templates/gamer-card.json";
import gatsbyQrTemplateJson from "./templates/gatsby.json";
import holidayQrTemplateJson from "./templates/holiday.json";
import horseQrTemplateJson from "./templates/horse.json";
import soccerQrTemplateJson from "./templates/soccer.json";
import starsQrTemplateJson from "./templates/stars.json";

export const QR_TEMPLATE_PREVIEW_BASE_URL =
  "https://kycacontent.s3.eu-central-1.amazonaws.com/guestgallery/qr-templates/a4";
export const QR_TEMPLATE_PREVIEW_LOCALES = [
  "en",
  "da",
  "no",
  "sv",
  "de",
] as const;

export type QrTemplatePreviewLocale =
  (typeof QR_TEMPLATE_PREVIEW_LOCALES)[number];
export type QrTemplatePreviewImageUrls = Partial<
  Record<QrTemplatePreviewLocale, string>
>;

export type StoredQrTemplateJson = {
  format: string;
  version: number;
  templateId?: string;
  templateName: string;
  exportedAt: string;
  config: {
    defaultVariantId: string;
    variants: Array<{
      id: string;
      name: string;
      style: "solid" | "gradient";
      swatchStart: string;
      swatchEnd: string;
      previewImageUrls?: QrTemplatePreviewImageUrls;
      config: {
        tags: string[];
        tasks: string[];
      } & Record<string, unknown>;
    }>;
  };
};

export interface StoredQrTemplatePreset {
  id: string;
  name: string;
  subtitle: string;
  previewImageKey: string;
  previewImageExtension: "png" | "webp" | "jpg" | "jpeg";
  locales: readonly string[];
  templateJson: StoredQrTemplateJson;
}

const storedBalloonsQrTemplateJson =
  balloonsQrTemplateJson as StoredQrTemplateJson;
const storedButterflyQrTemplateJson =
  butterflyQrTemplateJson as StoredQrTemplateJson;
const storedCameraQrTemplateJson =
  cameraQrTemplateJson as StoredQrTemplateJson;
const storedDefaultQrTemplateJson =
  defaultQrTemplateJson as StoredQrTemplateJson;
const storedEucalyptusQrTemplateJson =
  eucalyptusQrTemplateJson as StoredQrTemplateJson;
const storedFlowerQrTemplateJson = flowerQrTemplateJson as StoredQrTemplateJson;
const storedGamerCardQrTemplateJson =
  gamerCardQrTemplateJson as StoredQrTemplateJson;
const storedGatsbyQrTemplateJson = gatsbyQrTemplateJson as StoredQrTemplateJson;
const storedHolidayQrTemplateJson = holidayQrTemplateJson as StoredQrTemplateJson;
const storedHorseQrTemplateJson = horseQrTemplateJson as StoredQrTemplateJson;
const storedSoccerQrTemplateJson = soccerQrTemplateJson as StoredQrTemplateJson;
const storedStarsQrTemplateJson = starsQrTemplateJson as StoredQrTemplateJson;

export const STORED_QR_TEMPLATE_PRESETS = [
  {
    id: "balloons-qr-card",
    name: "balloons",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "balloons",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedBalloonsQrTemplateJson,
  },
  {
    id: "butterfly-qr-card",
    name: "Butterfly",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "butterfly",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedButterflyQrTemplateJson,
  },
  {
    id: "camera-qr-card",
    name: "Camera",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "camera",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedCameraQrTemplateJson,
  },
  {
    id: "default-qr-card",
    name: "Default QR Card",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "alt",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedDefaultQrTemplateJson,
  },
  {
    id: "eucalyptus-qr-card",
    name: "Eucalyptus",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "eucalyptus",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedEucalyptusQrTemplateJson,
  },
  {
    id: "flower-qr-card",
    name: "Flower",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "green_flower",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedFlowerQrTemplateJson,
  },
  {
    id: "gamer-card-qr-card",
    name: "Gamer card",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "gamer_card",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedGamerCardQrTemplateJson,
  },
  {
    id: "gatsby-qr-card",
    name: "Gatsby",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "gatsby",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedGatsbyQrTemplateJson,
  },
  {
    id: "holiday-qr-card",
    name: "holiday",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "holiday",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedHolidayQrTemplateJson,
  },
  {
    id: "horse-qr-card",
    name: "Horse",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "horse",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedHorseQrTemplateJson,
  },
  {
    id: "soccer-qr-card",
    name: "Soccer",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "soccer",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedSoccerQrTemplateJson,
  },
  {
    id: "stars-qr-card",
    name: "Stars",
    subtitle: "Editable print-ready QR card",
    previewImageKey: "stars",
    previewImageExtension: "png" as const,
    locales: ["en", "da", "no", "sv", "de"] as const,
    templateJson: storedStarsQrTemplateJson,
  },
] as const satisfies readonly StoredQrTemplatePreset[];
export const DEFAULT_STORED_QR_TEMPLATE_PRESET_ID = "default-qr-card";
const defaultStoredQrTemplatePreset =
  STORED_QR_TEMPLATE_PRESETS.find(
    (preset) => preset.id === DEFAULT_STORED_QR_TEMPLATE_PRESET_ID,
  ) ?? STORED_QR_TEMPLATE_PRESETS[0];
export const DEFAULT_QR_TEMPLATE_JSON =
  defaultStoredQrTemplatePreset.templateJson;
export const DEFAULT_QR_TEMPLATE_BASE_CONFIG =
  DEFAULT_QR_TEMPLATE_JSON.config.variants[0].config;
export const DEFAULT_QR_TEMPLATE_TASKS =
  DEFAULT_QR_TEMPLATE_BASE_CONFIG.tasks;

export function getStoredQrTemplatePresetById(
  presetId: string,
): StoredQrTemplatePreset | undefined {
  return STORED_QR_TEMPLATE_PRESETS.find((preset) => preset.id === presetId);
}

function resolvePreviewUrlCandidate(
  previewImageUrls: QrTemplatePreviewImageUrls | undefined,
  locale: string,
): string {
  if (!previewImageUrls) {
    return "";
  }

  const localizedPreviewUrl = previewImageUrls[locale as QrTemplatePreviewLocale];
  if (typeof localizedPreviewUrl === "string" && localizedPreviewUrl.length > 0) {
    return localizedPreviewUrl;
  }

  const englishPreviewUrl = previewImageUrls.en;
  if (typeof englishPreviewUrl === "string" && englishPreviewUrl.length > 0) {
    return englishPreviewUrl;
  }

  for (const previewLocale of QR_TEMPLATE_PREVIEW_LOCALES) {
    const previewUrl = previewImageUrls[previewLocale];
    if (typeof previewUrl === "string" && previewUrl.length > 0) {
      return previewUrl;
    }
  }

  return "";
}

export function getStoredQrTemplateVariantPreviewImageUrl(
  variant: { previewImageUrls?: QrTemplatePreviewImageUrls },
  locale: string,
): string {
  return resolvePreviewUrlCandidate(variant.previewImageUrls, locale);
}

export function getStoredQrTemplatePreviewImageUrl(
  preset: Pick<
    StoredQrTemplatePreset,
    "previewImageKey" | "previewImageExtension" | "locales" | "templateJson"
  >,
  locale: string,
): string {
  const defaultVariant =
    preset.templateJson.config.variants.find(
      (variant) => variant.id === preset.templateJson.config.defaultVariantId,
    ) ?? preset.templateJson.config.variants[0];
  const variantPreviewUrl = getStoredQrTemplateVariantPreviewImageUrl(
    defaultVariant,
    locale,
  );
  if (variantPreviewUrl.length > 0) {
    return variantPreviewUrl;
  }

  const locales = preset.locales as readonly string[];
  const previewLocale = locales.includes(locale) ? locale : "en";
  return `${QR_TEMPLATE_PREVIEW_BASE_URL}/${preset.previewImageKey}_${previewLocale}.${preset.previewImageExtension}`;
}
